import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  InlineError,
  Button,
  Thumbnail,
  DropZone,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import { MultiSelect } from "react-multi-select-component";
import { EditIcon } from "@shopify/polaris-icons";

const AddEditTaskManager = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id, taskId } = useParams();

  const [isSaving, setIsSaving] = useState(mode === "edit");
  const [isUploading, setIsUploading] = useState(false);

  const [projectOptions, setProjectOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [peopleOptions, setPeopleOptions] = useState([]);

  const [files, setFiles] = useState([]);
  const validImageTypes = ["image/png", "image/jpeg", "image/webp"];
  const uploadedKeys = useRef([]);

  const [item, setItem] = useState({
    title: "",
    description: "",
    status: "",
    category: "",
    type: "",
    country: "All",
    assignees: [],
    reporters: [],
    deadline: "",
    screenshots: [],
    priority: "0",
  });

  const [errors, setErrors] = useState({});
  const [toastActive, setToastActive] = useState(false);
  const toggleToast = useCallback(() => setToastActive((a) => !a), []);
  const toastMarkup = toastActive ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleToast}
    />
  ) : null;

  const [existingScreenshots, setExistingScreenshots] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const defaultStatusFromMain = queryParams.get("status") || "";

  // ✅ Fetch options
  useEffect(() => {
    axios.get("/settings/countries").then((res) => {
      const data = res?.data?.data || [];
      const options = [
        { label: "All", value: "All" },
        ...data.map((country) => ({
          label: country.name,
          value: country.name,
        })),
      ];
      setCountryOptions(options);
    });

    axios.get("/people?all").then((res) => {
      const data = res?.data?.data || [];
      setPeopleOptions(
        data.map((p) => ({
          label: p.name,
          value: String(p.id),
        }))
      );
    });

    axios.get("/projects?all").then((res) => {
      const data = res?.data?.data || [];
      setProjectOptions(
        data.map((p) => ({ label: p.name, value: String(p.id) }))
      );
    });

    axios.get(`/task-manager-projects/${id}/statuses`).then((res) => {
      const data = res?.data?.data?.data || [];
      setStatusOptions(
        data.map((s) => ({ label: s.name, value: String(s.id) }))
      );
      if (mode === "new" && defaultStatusFromMain) {
        setItem((prev) => ({ ...prev, status: String(defaultStatusFromMain) }));
      }
    });

    axios.get(`/task-manager-projects/${id}/categories`).then((res) => {
      const data = res?.data?.data?.data || [];
      setCategoryOptions(
        data.map((c) => ({ label: c.title, value: String(c.id) }))
      );
    });

    if (mode === "edit") {
      setIsSaving(true);
      axios
        .get(`/task-manager/${taskId}`)
        .then((res) => {
          const data = res?.data?.data || {};
          setItem({
            title: data.title || "",
            description: data.description || "",
            status: String(data.status_id) || "",
            category: String(data.category_id) || "",
            priority: String(data.is_high) || "0",
            country: data.country || "",
            assignees:
              data.assignees?.map((m) => ({
                label: m.name,
                value: String(m.id),
              })) || [],
            // reporters:
            //   data.reporters?.map((m) => ({
            //     label: m.name,
            //     value: String(m.id),
            //   })) || [],
            // deadline: data.deadline || "",
            screenshots: data.images || [],
          });
          if (data.screenshots && data.screenshots_keys) {
            console.log("okkk");
            const combined = data.screenshots.map((url, i) => ({
              url,
              key: data.screenshots_keys[i],
            }));
            setExistingScreenshots(combined);
          }
        })
        .finally(() => setIsSaving(false));
    }
  }, []);

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const handleDropZone = useCallback(async (_dropFiles, acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setIsUploading(true);
    try {
      setFiles((prev) => [...prev, ...acceptedFiles]);
      const uploads = acceptedFiles.map((file) => {
        const form_data = new FormData();
        form_data.append("entity_type", "task_manager_screenshot");
        form_data.append("image", file);
        return axios.post("/s3/save-file", form_data);
      });
      const results = await Promise.all(uploads);
      const newKeys = results.map((r) => r.data.data.file_key);
      uploadedKeys.current = [...uploadedKeys.current, ...newKeys];
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const validateFields = () => {
    const required = [
      "title",
      "category",
      // "description",
      // "status",
      // "category",
      // "country",
      // "assignees",
      // "reporters",
      // "deadline",
    ];
    const newErrors = {};
    required.forEach((field) => {
      if (
        !item[field] ||
        (Array.isArray(item[field]) && item[field].length === 0)
      ) {
        newErrors[field] = "This field is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateFields()) return;
    setIsSaving(true);

    const formData = new FormData();
    if (mode === "edit") formData.append("_method", "PATCH");

    formData.append("title", item.title);
    formData.append("description", item.description);
    formData.append("status_id", item.status);
    formData.append("category_id", item.category);
    formData.append("country", item.country);
    formData.append("deadline", item.deadline);
    formData.append("task_manager_project_id", id);
    formData.append("is_high", Number(item.priority));
    item.assignees.forEach((member, i) =>
      formData.append(`assignee_ids[${i}]`, member.value)
    );

    // item.reporters.forEach((member, i) =>
    //   formData.append(`reporter_ids[${i}]`, member.value)
    // );

    existingScreenshots.forEach((img, i) => {
      formData.append(`images[${i}]`, img.key);
    });

    uploadedKeys.current.forEach((key, i) => {
      formData.append(`images[${existingScreenshots.length + i}]`, key);
    });

    axios
      .post(
        mode === "new" ? `/task-manager` : `/task-manager/${taskId}`,
        formData
      )
      .then(() => {
        if (mode === "new") {
          navigate(`/admin/task-manager/projects/${id}/tasks`);
        } else {
          toggleToast();
        }
      })
      .catch(console.error)
      .finally(() => setIsSaving(false));
  };

  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/task-manager/${taskId}/${taskId}`)
      .then(() => {
        navigate(`/admin/task-manager/projects/${id}/tasks`);
      })
      .catch(console.error);
  };

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Task`}
      backAction={{
        onAction: () => navigate(`/admin/task-manager/projects/${id}/tasks`),
      }}
    >
      <Card sectioned>
        <FormLayout>
          <TextField
            label="Title"
            value={item.title}
            onChange={(v) => handleChange("title", v)}
            error={errors.title}
          />
          <Select
            label="Is high priority?"
            options={[
              { label: "Yes", value: "1" },
              { label: "No", value: "0" },
            ]}
            value={item.priority}
            onChange={(v) => handleChange("priority", v)}
          />
          {/* ✅ Assignees */}
          <Text>Assignees</Text>
          <div style={{ position: "relative", zIndex: 9999 }}>
            <MultiSelect
              options={peopleOptions}
              value={item.assignees}
              onChange={(v) => handleChange("assignees", v)}
              labelledBy="Select Assignees"
            />
            {errors.assignees && <InlineError message={errors.assignees} />}
          </div>

          {/* ✅ Reporters */}
          {/* <Text>Reporters</Text>
          <div style={{ position: "relative", zIndex: 9998 }}>
            <MultiSelect
              options={peopleOptions}
              value={item.reporters}
              onChange={(v) => handleChange("reporters", v)}
              labelledBy="Select Reporters"
            />
            {errors.reporters && <InlineError message={errors.reporters} />}
          </div> */}

          <TextField
            label="Description"
            value={item.description}
            onChange={(v) => handleChange("description", v)}
            error={errors.description}
          />

          <Select
            label="Status"
            options={statusOptions}
            value={item.status}
            onChange={(v) => handleChange("status", v)}
            error={errors.status}
            placeholder="Please select"
            disabled={defaultStatusFromMain}
            labelAction={{
              content: "Edit Statuses",
              onAction: () =>
                navigate(`/admin/task-manager/projects/${id}/statuses`),
              icon: EditIcon,
            }}
          />

          <Select
            label="Category"
            options={categoryOptions}
            value={item.category}
            onChange={(v) => handleChange("category", v)}
            error={errors.category}
            placeholder="Please select"
            labelAction={{
              content: "Edit Categories",
              onAction: () =>
                navigate(`/admin/task-manager/projects/${id}/categories`),
              icon: EditIcon,
            }}
          />

          <Select
            label="Country"
            options={countryOptions}
            value={item.country}
            onChange={(v) => handleChange("country", v)}
            error={errors.country}
            placeholder="Please select"
          />

          <TextField
            label="Deadline"
            type="date"
            value={item.deadline}
            onChange={(v) => handleChange("deadline", v)}
            error={errors.deadline}
          />

          <Text as="p">Screenshots</Text>

          <DropZone onDrop={handleDropZone} allowMultiple accept="image/*">
            <DropZone.FileUpload />
          </DropZone>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              marginTop: "10px",
            }}
          >
            {existingScreenshots.map((img, index) => (
              <div key={`existing-${index}`} style={{ position: "relative" }}>
                <Thumbnail source={img.url} size="small" />
                <Button
                  tone="critical"
                  size="slim"
                  onClick={() =>
                    setExistingScreenshots((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "-8px",
                    borderRadius: "50%",
                    minWidth: "24px",
                    height: "24px",
                  }}
                >
                  ×
                </Button>
              </div>
            ))}

            {files.map((file, index) => (
              <div
                key={`new-${index}`}
                style={{ position: "relative", width: 100, height: 100 }}
              >
                <Thumbnail source={URL.createObjectURL(file)} size="small" />
                <Button
                  tone="critical"
                  size="slim"
                  onClick={() =>
                    setFiles((prev) => prev.filter((_, i) => i !== index))
                  }
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "-8px",
                    borderRadius: "50%",
                    minWidth: "24px",
                    height: "24px",
                  }}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              tone="critical"
              onClick={() => setActiveDelete(true)}
              loading={isSaving}
            >
              Delete
            </Button>
          )
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            onAction: toggleDeleteModal,
            loading: isSaving,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {isUploading && <LoadingScreen />}
    </Page>
  );
};

export default AddEditTaskManager;
